<template>
  <div v-if="show" class="pw-reminder-modal-overlay" @click.self="noop">
    <div class="pw-reminder-modal">
      <div class="modal-header">
        <h4 class="modal-title">
          <i class="fa fa-shield" style="color: #007bff; margin-right: 10px;"></i>
          비밀번호 변경 권장
        </h4>
      </div>
      <div class="modal-body">
        <p class="reminder-desc">
          마지막 비밀번호 변경 후 90일이 지났습니다. 계정 보안을 위해 비밀번호 변경을 권장합니다.
        </p>
        <form @submit.prevent="submitChange">
          <div class="form-group">
            <label>현재 비밀번호</label>
            <input
              v-model="form.password"
              type="password"
              class="form-control"
              autocomplete="current-password"
              required
            />
          </div>
          <div class="form-group">
            <label>새 비밀번호</label>
            <input
              v-model="form.new_password"
              :class="inputPasswordClasses(form.new_password)"
              type="password"
              class="form-control"
              autocomplete="new-password"
              required
            />
            <div class="invalid-feedback">
              <div v-if="!validPasswordLength(form.new_password)"> 8자 이상이어야 합니다. </div>
              <div v-if="!validPasswordUppercase(form.new_password)"> 대문자 1자 이상 포함. </div>
              <div v-if="!validPasswordLowercase(form.new_password)"> 소문자 1자 이상 포함. </div>
              <div v-if="!validPasswordNumber(form.new_password)"> 숫자 1자 이상 포함. </div>
              <div v-if="!validPasswordSpecial(form.new_password)"> 특수문자 1자 이상 포함. </div>
            </div>
          </div>
          <div class="form-group">
            <label>새 비밀번호 확인</label>
            <input
              v-model="form.confirm_password"
              :class="{
                'is-valid':
                  form.confirm_password.length > 0 &&
                  form.confirm_password === form.new_password
              }"
              type="password"
              class="form-control"
              autocomplete="new-password"
            />
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button class="btn btn-light" :disabled="submitting" @click="snooze">
          30일간 보지 않기
        </button>
        <button
          class="btn btn-primary"
          :class="{ disabled: !changeValid || submitting }"
          :disabled="!changeValid || submitting"
          @click="submitChange"
        >
          지금 변경
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import toastrs from '@/mixins/toastrs'

export default {
  name: 'PasswordChangeReminderModal',
  mixins: [toastrs],
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      submitting: false,
      form: {
        password: '',
        new_password: '',
        confirm_password: ''
      }
    }
  },
  computed: {
    changeValid() {
      if (!this.validPassword(this.form.new_password)) return false
      if (this.form.password.length === 0) return false
      if (this.form.new_password !== this.form.confirm_password) return false
      return true
    }
  },
  methods: {
    noop() {
      // overlay 클릭으로 닫히지 않음 (지금 변경 또는 30일 스누즈만)
    },
    validPassword(password) {
      if (password.length < 8) return false
      if (!/[a-z]/.test(password)) return false
      if (!/[A-Z]/.test(password)) return false
      if (!/[0-9]/.test(password)) return false
      if (!this.validPasswordSpecial(password)) return false
      return true
    },
    validPasswordLength(password) {
      return password.length >= 8
    },
    validPasswordLowercase(password) {
      return /[a-z]/.test(password)
    },
    validPasswordUppercase(password) {
      return /[A-Z]/.test(password)
    },
    validPasswordNumber(password) {
      return /[0-9]/.test(password)
    },
    validPasswordSpecial(password) {
      // 백엔드 화이트리스트(api/users.py)와 동일 집합: !@#$%^&*()-_=+
      return /[!@#$%^&*()_+=-]/.test(password)
    },
    inputPasswordClasses(password) {
      let isValid = this.validPassword(password)
      return {
        'is-invalid': !isValid && password.length != 0,
        'is-valid': isValid
      }
    },
    submitChange() {
      if (!this.changeValid || this.submitting) return
      this.submitting = true
      axios
        .post('/api/user/password', {
          password: this.form.password,
          new_password: this.form.new_password
        })
        .then(() => {
          this.submitting = false
          this.resetForm()
          this.axiosReqestSuccess('비밀번호 변경', '비밀번호가 변경되었습니다.')
          this.$emit('changed')
        })
        .catch(error => {
          this.submitting = false
          let msg = (error.response && error.response.data && error.response.data.message) || '비밀번호 변경에 실패했습니다.'
          this.axiosReqestError('비밀번호 변경', msg)
        })
    },
    snooze() {
      if (this.submitting) return
      this.submitting = true
      axios
        .post('/api/user/password/snooze-reminder')
        .then(() => {
          this.submitting = false
          this.$emit('snoozed')
        })
        .catch(error => {
          this.submitting = false
          let msg = (error.response && error.response.data && error.response.data.message) || '요청에 실패했습니다.'
          this.axiosReqestError('비밀번호 변경 알림', msg)
        })
    },
    resetForm() {
      this.form.password = ''
      this.form.new_password = ''
      this.form.confirm_password = ''
    }
  }
}
</script>

<style scoped>
.pw-reminder-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  /* SessionExpiredModal(9999)보다 한 단계 아래 — 동시 표출 시 세션만료 모달 우선 */
  z-index: 9998;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.pw-reminder-modal {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 480px;
  width: 90%;
  text-align: left;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from { transform: translateY(-50px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e5e5;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
}

.modal-body {
  padding: 24px;
  color: #444;
}

.reminder-desc {
  margin-bottom: 18px;
  color: #666;
  line-height: 1.5;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-light {
  background-color: #f1f1f1;
  color: #333;
}

.btn-light:hover {
  background-color: #e2e2e2;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn.disabled,
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
